(function () {
  'use strict';

  var EFFECTS = ['none', 'rolling', 'cascade'];
  var DIRECTIONS = ['top', 'left', 'right', 'bottom'];
  var initialized = new WeakMap();
  var contextEffects = {};

  function isRoot(value) {
    return value === document || value instanceof Element || value instanceof DocumentFragment;
  }

  function normalizeEffect(value) {
    var normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'default') return 'none';
    return EFFECTS.indexOf(normalized) > -1 ? normalized : '';
  }

  function normalizeDirection(value) {
    var normalized = String(value || '').trim().toLowerCase();
    return DIRECTIONS.indexOf(normalized) > -1 ? normalized : 'bottom';
  }

  function resolveContext(label) {
    var context = label.dataset.motionLabelContext || 'navigation';
    if (context !== 'auto') return context;

    var trigger = label.closest('.motion-label-trigger, [data-motion-label-trigger]');
    if (!trigger) return 'button';
    if (trigger.matches('.btn-primary, .btn-secondary, .btn-outline, .btn-sm, .button, button, [role="button"]')) {
      return 'button';
    }
    return trigger.matches('a') ? 'link' : 'button';
  }

  function getContextEffect(context) {
    var body = document.body;
    var configured = contextEffects[context];
    if (configured) return configured;
    if (!body) return 'none';
    if (context === 'button') return normalizeEffect(body.dataset.buttonAnimation) || 'none';
    if (context === 'link') return normalizeEffect(body.dataset.linkAnimation) || 'none';
    return normalizeEffect(body.dataset.navigationLinkAnimation) || 'none';
  }

  function getContextDirection(context) {
    var body = document.body;
    if (!body) return 'bottom';
    if (context === 'button') return normalizeDirection(body.dataset.buttonAnimationDirection);
    if (context === 'link') return normalizeDirection(body.dataset.linkAnimationDirection);
    return normalizeDirection(body.dataset.navigationLinkAnimationDirection);
  }

  function resolveDirection(label, context) {
    var explicit = label.dataset.motionLabelDirectionSetting;
    return explicit ? normalizeDirection(explicit) : getContextDirection(context);
  }

  function resolveEffect(label) {
    var explicit = normalizeEffect(label.dataset.motionLabelEffect);
    if (explicit) return explicit;

    var contextRoot = label.parentElement
      ? label.parentElement.closest('[data-motion-label-context][data-motion-label-effect]')
      : null;
    if (contextRoot) {
      var inherited = normalizeEffect(contextRoot.dataset.motionLabelEffect);
      if (inherited) return inherited;
    }

    return getContextEffect(resolveContext(label));
  }

  // Characters that can join their neighbour into one grapheme (combining marks, joiners, emoji,
  // flags, skin tones, Hangul jamo, CRLF). Text without them splits into graphemes exactly per code
  // point, so the much slower Intl.Segmenter is only used when it can change the result.
  var GRAPHEME_EXTENDERS = /[\p{M}\p{Extended_Pictographic}‍\rᄀ-ᇿ\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}]/u;
  var graphemeSegmenter = null;

  function segmentText(text) {
    if (!GRAPHEME_EXTENDERS.test(text) || !(window.Intl && typeof window.Intl.Segmenter === 'function')) {
      return Array.from(text);
    }
    graphemeSegmenter = graphemeSegmenter
      || new window.Intl.Segmenter(document.documentElement.lang || undefined, { granularity: 'grapheme' });
    return Array.from(graphemeSegmenter.segment(text), function (entry) {
      return entry.segment;
    });
  }

  function clearGenerated(label) {
    var canonical = label.querySelector('[data-motion-label-canonical]');
    var track = canonical && canonical.parentElement;

    if (track && track.classList.contains('motion-label__track')) {
      label.insertBefore(canonical, track);
      track.remove();
    }

    label.querySelectorAll('[data-motion-label-generated]').forEach(function (node) {
      node.remove();
    });
    label.classList.remove('motion-label--none', 'motion-label--rolling', 'motion-label--cascade');
  }

  function buildRolling(label, canonical) {
    var track = document.createElement('span');
    var duplicate = document.createElement('span');

    track.className = 'motion-label__track';
    track.dataset.motionLabelGenerated = '';
    duplicate.className = 'motion-label__duplicate';
    duplicate.dataset.motionLabelGenerated = '';
    duplicate.setAttribute('aria-hidden', 'true');
    duplicate.textContent = canonical.textContent;

    canonical.before(track);
    track.append(canonical, duplicate);
  }

  function buildCascade(label, canonical) {
    var cascade = document.createElement('span');
    cascade.className = 'motion-label__cascade';
    cascade.dataset.motionLabelGenerated = '';
    cascade.setAttribute('aria-hidden', 'true');

    segmentText(canonical.textContent || '').forEach(function (character, index) {
      var span = document.createElement('span');
      span.className = 'motion-label__character';
      span.style.setProperty('--motion-label-index', index);
      span.textContent = character;
      cascade.append(span);
    });

    label.append(cascade);
  }

  function initializeLabel(label) {
    if (!(label instanceof Element)) return;
    var canonical = label.querySelector('[data-motion-label-canonical]');
    if (!canonical) return;

    var effect = resolveEffect(label);
    var context = resolveContext(label);
    var direction = resolveDirection(label, context);
    var signature = effect + ':' + context + ':' + direction;
    if (initialized.get(label) === signature) return;

    clearGenerated(label);
    label.classList.add('motion-label--' + effect);
    label.dataset.motionLabelResolvedContext = context;
    label.dataset.motionLabelDirection = direction;

    if (effect === 'rolling') buildRolling(label, canonical);
    if (effect === 'cascade') buildCascade(label, canonical);

    initialized.set(label, signature);
    label.dataset.motionLabelReady = 'true';
  }

  function initializeRoot(root) {
    var scope = isRoot(root) ? root : document;
    if (scope instanceof Element && scope.matches('[data-motion-label]')) initializeLabel(scope);
    scope.querySelectorAll('[data-motion-label]').forEach(initializeLabel);
    return scope;
  }

  function setMotionLabel(target, text, context) {
    if (!(target instanceof Element)) return null;
    var label = target.matches('[data-motion-label]')
      ? target
      : target.querySelector('[data-motion-label]');
    if (!label) return null;

    var canonical = label.querySelector('[data-motion-label-canonical]');
    if (!canonical) return null;

    var nextText = text == null ? '' : String(text);
    if (canonical.textContent === nextText && (!context || label.dataset.motionLabelContext === context)) {
      initializeLabel(label);
      return label;
    }
    canonical.textContent = nextText;
    if (context === 'button' || context === 'navigation') {
      label.dataset.motionLabelContext = context;
    }

    initialized.delete(label);
    initializeLabel(label);
    return label;
  }

  function configureMotionLabels(context, effect, root) {
    var normalizedContext = context === 'button' || context === 'link' ? context : 'navigation';
    var normalizedEffect = normalizeEffect(effect);
    if (normalizedEffect) contextEffects[normalizedContext] = normalizedEffect;
    return initializeRoot(root || document);
  }

  window.Axiom = window.Axiom || {};
  window.Axiom.setMotionLabel = setMotionLabel;
  window.Axiom.configureMotionLabels = configureMotionLabels;
  window.Axiom.initMotionLabels = initializeRoot;
  window.Axiom.requestMotionRefresh = initializeRoot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeRoot(document);
    }, { once: true });
  } else {
    initializeRoot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeRoot(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    initializeRoot(event.target);
  });
})();
